(() => {
  'use strict';
  // Visual references: React Bits Blur Text, Tilted Card, Glare Hover, Scroll Reveal.
  // This is an original, dependency-free implementation for Shopify Liquid.
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const root = document.documentElement;
  const played = new WeakSet();
  const animations = new Set();
  let cleanup = [], rebuildQueued = false;
  const canAnimate = () => !reduced.matches && !window.Shopify?.designMode;
  const all = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  function listen(target, event, fn, options) {
    target.addEventListener(event, fn, options);
    cleanup.push(() => target.removeEventListener(event, fn, options));
  }
  function animate(el, frames, options) {
    if (!canAnimate() || !el.animate) return;
    const a = el.animate(frames, options); animations.add(a);
    a.onfinish = a.oncancel = () => animations.delete(a);
  }
  function observe(el, callback, threshold = .12) {
    if (!('IntersectionObserver' in window)) { callback(true); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(e => callback(e.isIntersecting)), { threshold });
    observer.observe(el); cleanup.push(() => observer.disconnect());
  }
  function splitHeading(el) {
    if (el.dataset.vx2Split || el.children.length) return;
    const text = el.textContent.trim();
    const words = text.split(/\s+/);
    if (!text || words.length > 28) return;
    const fragment = document.createDocumentFragment();
    words.forEach((word, i) => {
      const span = document.createElement('span'); span.className = 'vx2-word';
      span.textContent = word; span.setAttribute('aria-hidden', 'true'); fragment.append(span);
      if (i < words.length - 1) fragment.append(document.createTextNode(' '));
    });
    el.setAttribute('aria-label', text); el.replaceChildren(fragment); el.dataset.vx2Split = 'true';
  }
  function enterHeading(el) {
    if (played.has(el) || !canAnimate()) return;
    played.add(el);
    const words = all('.vx2-word', el);
    const hero=!!el.closest('.vxh-hero');
    (words.length ? words : [el]).forEach((word, i) => animate(word,
      [{ opacity: hero?.2:.45, filter: 'blur(5px)', transform: hero?'translateY(32px) rotateX(-55deg)':'translateY(14px)' },
       { opacity: 1, filter: 'blur(0px)', transform: 'translateY(0) rotateX(0)' }],
      { duration: hero?1200:850, delay: Math.min(i * (hero?85:45), 480), easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' }));
  }
  function init() {
    cleanup.forEach(fn => fn()); cleanup = [];
    [...animations].forEach(a => a.cancel());
    const enabled = canAnimate(); root.dataset.vx2Motion = enabled ? 'on' : 'off';
    root.dataset.vx2Hidden = String(document.hidden);
    all('.vxh h1,.vxh-heading h2,.vxh-season h2,.vxh-story h2,.varelix-journal-heading h2,.vxi-collection-copy h1').forEach(el => {
      splitHeading(el); if (enabled) observe(el, visible => { if (visible) enterHeading(el); });
    });
    all('.vxh-category,.vxh-product,.varelix-journal-card').forEach((el, i) => {
      if (!enabled) return;
      observe(el, visible => {
        if (!visible || played.has(el)) return; played.add(el);
        animate(el, [{opacity:.5,translate:'0 24px'},{opacity:1,translate:'0 0'}],
          {duration:900,delay:(i%4)*65,easing:'cubic-bezier(.16,1,.3,1)'});
      });
    });
    all('.vxh-hero-copy,.vxh-story,.vx2-ribbon').forEach(el => observe(el, visible => el.dataset.vx2Visible = String(visible)));
    all('.vxh-hero-copy,#VarelixCategories .vxh-heading,.vxh-season-grid>div:first-child,.vxh-spaces .vxh-heading,.vxh-story-grid>div:first-child').forEach(el=>{
      if (!enabled) {el.classList.remove('vx2-leaf-enter');return;}
      observe(el,visible=>{if(visible)el.classList.add('vx2-leaf-enter');});
    });
    // Tilt only decorative image containers, keeping names, prices and buttons stationary.
    all('.vxh-category-media,.varelix-journal-image,.vxi-collection-photo').forEach(el => {
      el.classList.add('vx2-tilt');
      ['--vx2-rx','--vx2-ry','--vx2-x','--vx2-y'].forEach(p=>el.style.removeProperty(p));
      delete el.dataset.vx2Hover;
      if (!enabled || !fine.matches) return;
      let frame=0, px=.5, py=.5;
      const reset=()=>{cancelAnimationFrame(frame);frame=0;el.style.setProperty('--vx2-rx','0deg');el.style.setProperty('--vx2-ry','0deg');el.dataset.vx2Hover='false';};
      listen(el,'pointermove',event=>{
        if(event.pointerType==='touch')return;
        const r=el.getBoundingClientRect();px=Math.max(0,Math.min(1,(event.clientX-r.left)/r.width));py=Math.max(0,Math.min(1,(event.clientY-r.top)/r.height));
        if(frame)return;
        frame=requestAnimationFrame(()=>{frame=0;el.style.setProperty('--vx2-rx',`${(0.5-py)*4}deg`);el.style.setProperty('--vx2-ry',`${(px-0.5)*4}deg`);el.style.setProperty('--vx2-x',`${px*100}%`);el.style.setProperty('--vx2-y',`${py*100}%`);el.dataset.vx2Hover='true';});
      },{passive:true});
      listen(el,'pointerleave',reset);cleanup.push(reset);
    });
    // Product image slideshow: each card pauses while being inspected.
    all('.vxh-products').forEach(grid=>{
      const section=grid.closest('section');
      const cards=all('.vxh-product-media',section).filter(c=>c.querySelectorAll('.vxh-slide').length>1);
      let visible=false,timer;
      const stop=()=>{clearInterval(timer);timer=null;};
      const sync=()=>{stop();if(!enabled||!visible||document.hidden||!cards.length)return;
        timer=setInterval(()=>cards.forEach(card=>{
          if(card.matches(':hover,:focus-within'))return;
          const slides=all('.vxh-slide',card);const current=slides.findIndex(s=>s.classList.contains('is-active'));let next=current;
          for(let step=1;step<slides.length;step++){const candidate=(current+step)%slides.length;const img=slides[candidate].querySelector('img');if(img?.complete&&img.naturalWidth){next=candidate;break;}}
          if(next===current)return;
          slides.forEach((s,i)=>{s.classList.toggle('is-active',i===next);s.setAttribute('aria-hidden',String(i!==next));});
        }),7000);
      };
      listen(document,'visibilitychange',sync);observe(section,v=>{visible=v;sync();});cleanup.push(stop);
    });
    all('.vxh-story-scroll').forEach(box=>{
      box.classList.toggle('is-moving',enabled);
      observe(box,v=>box.dataset.vx2Visible=String(v));
    });
    // One bounded scroll callback; no continuous JS animation loop.
    all('.vxh-season-image').forEach(el=>el.style.removeProperty('--vx2-drift'));
    let scrollFrame=0;
    if(enabled&&fine.matches){
      const scroll=()=>{if(scrollFrame||document.hidden)return;scrollFrame=requestAnimationFrame(()=>{scrollFrame=0;all('.vxh-season-image').forEach(el=>{const r=el.getBoundingClientRect();if(r.bottom>0&&r.top<innerHeight)el.style.setProperty('--vx2-drift',`${Math.max(-10,Math.min(10,(innerHeight/2-r.top-r.height/2)*.025))}px`);});});};
      listen(window,'scroll',scroll,{passive:true});const wrapper=document.querySelector('.page-wrapper');if(wrapper)listen(wrapper,'scroll',scroll,{passive:true});scroll();cleanup.push(()=>cancelAnimationFrame(scrollFrame));
    }
    listen(document,'visibilitychange',()=>{root.dataset.vx2Hidden=String(document.hidden);if(document.hidden)[...animations].forEach(a=>a.finish());});
  }
  function queueInit(){if(rebuildQueued)return;rebuildQueued=true;queueMicrotask(()=>{rebuildQueued=false;init();});}
  reduced.addEventListener('change',queueInit);fine.addEventListener('change',queueInit);
  document.addEventListener('shopify:section:load',queueInit);document.addEventListener('shopify:section:unload',queueInit);
  init();
})();
